import { Link } from "react-router-dom";
import { BookOpen, CalendarDays, ExternalLink, HelpCircle, Inbox, LifeBuoy, Link2, Mail, Newspaper, Printer } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT } from "@/lib/site";

const GUIDES = [
  {
    id: "blog",
    icon: Newspaper,
    title: "Managing blog posts",
    steps: [
      "Go to the Blog tab and click \u201cNew post\u201d.",
      "Give it a title \u2014 the web address (slug) is created automatically.",
      "Write your post. Blank lines create new paragraphs; lines starting with \u201c## \u201d become subheadings.",
      "Optionally paste a cover image URL for the picture at the top of the post.",
      "Toggle \u201cPublished\u201d on and save \u2014 it appears on the website instantly.",
      "To fix a typo later, click the pencil icon next to the post, edit and save.",
    ],
    tip: "Save a post with \u201cPublished\u201d toggled off to keep it as a private draft until it's ready.",
  },
  {
    id: "timetable",
    icon: CalendarDays,
    title: "Updating the timetable",
    steps: [
      "Go to the Timetable tab and click \u201cAdd class\u201d (or the pencil to edit one).",
      "Fill in the class name, style, day, start and end times, age group, level and venue.",
      "Save \u2014 the public timetable updates straight away, including the printable version.",
      "To remove a class, click the bin icon and confirm.",
    ],
    tip: "Parents' \u201cAdd to calendar\u201d buttons and shared links always use the latest times \u2014 no extra steps needed.",
  },
  {
    id: "enquiries",
    icon: Inbox,
    title: "Handling costume enquiries",
    steps: [
      "When a parent asks about a costume on the website, it lands in the Enquiries tab AND you get an email at Info@Kerrys.Dance.",
      "Reply straight from your email (just hit Reply \u2014 it goes to the parent), or click their email/phone in the Enquiries tab.",
      "Once you've dealt with one, click \u201cMark handled\u201d so you know it's done.",
      "Changed your mind? \u201cReopen\u201d puts it back. \u201cDelete\u201d removes it for good.",
    ],
    tip: "The dashboard shows how many new enquiries are waiting the moment you log in.",
  },
  {
    id: "sharing",
    icon: Link2,
    title: "Sharing & printing the timetable",
    steps: [
      "On the public timetable page, pick a filter (e.g. Tuesday) and click \u201cCopy link\u201d \u2014 paste it on Facebook or into a message and it opens already filtered.",
      "Click \u201cPrint timetable\u201d for a clean, fridge-ready sheet \u2014 or choose \u201cSave as PDF\u201d in the print window to get a file you can share.",
      "Parents can use each class's \u201cCalendar\u201d button to add it to their phone calendar as a weekly event.",
    ],
    tip: "Great for term-start posts: filter to one day, copy the link, and share it with that day's class groups.",
  },
];

export default function AdminHelp() {
  return (
    <div data-testid="admin-help-page">
      <div className="flex items-start gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Help &amp; how-to</h1>
          <p className="text-muted-foreground mt-1">Everything you need to run the website — no tech skills required.</p>
        </div>
      </div>

      <Card className="rounded-[4px] mt-6 border-[hsl(var(--kerrys-coral))]/50">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-[4px] bg-[hsl(var(--kerrys-sun-pale))] flex items-center justify-center shrink-0">
              <HelpCircle className="h-5 w-5 text-[hsl(38,80%,35%)]" />
            </span>
            <h2 className="font-display text-xl sm:text-2xl font-semibold">The basics</h2>
          </div>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed list-disc pl-5 marker:text-[hsl(var(--kerrys-coral))]">
            <li>
              Log in any time at <span className="font-semibold">www.kerrys.dance/admin</span> (or via the
              &ldquo;Staff login&rdquo; link at the bottom of every page). You stay logged in for 7 days.
            </li>
            <li>Everything you change here appears on the public website immediately — no publishing step, no waiting.</li>
            <li>You can't break anything by looking around. Deleting asks for confirmation first.</li>
          </ul>
        </CardContent>
      </Card>

      <div className="mt-6">
        <Accordion type="single" collapsible className="space-y-3" data-testid="admin-help-accordion">
          {GUIDES.map((g) => (
            <AccordionItem
              key={g.id}
              value={g.id}
              className="border rounded-[4px] bg-card px-5 sm:px-6 data-[state=open]:border-[hsl(var(--kerrys-sea))]/50"
              data-testid={`admin-help-section-${g.id}`}
            >
              <AccordionTrigger className="hover:no-underline py-4">
                <span className="flex items-center gap-3 text-left">
                  <g.icon className="h-5 w-5 text-[hsl(var(--kerrys-sea))] shrink-0" />
                  <span className="font-display text-lg sm:text-xl font-semibold">{g.title}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <ol className="space-y-2.5 text-sm leading-relaxed list-decimal pl-5 marker:font-bold marker:text-[hsl(var(--kerrys-coral))]">
                  {g.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
                <div className="mt-4 flex items-start gap-2.5 bg-[hsl(var(--kerrys-sun-pale))] border rounded-[4px] px-4 py-3">
                  <BookOpen className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(38,80%,35%)]" />
                  <p className="text-sm leading-relaxed"><span className="font-semibold">Tip:</span> {g.tip}</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-6">
        <Card className="rounded-[4px]">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <ExternalLink className="h-5 w-5 text-[hsl(var(--kerrys-sea))]" />
              <h2 className="font-display text-lg sm:text-xl font-semibold">Bookings (ClassForKids)</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Class bookings and payments are handled by ClassForKids, not this admin panel. Manage places, waiting
              lists and payments in your ClassForKids account — the website's &ldquo;Book&rdquo; page sends parents
              there automatically.
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-[4px]">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <LifeBuoy className="h-5 w-5 text-[hsl(var(--kerrys-sea))]" />
              <h2 className="font-display text-lg sm:text-xl font-semibold">Stuck on something?</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              If something looks wrong on the website or you're not sure how to do something, drop a note to your
              website manager — or start with a quick look at the public site to see how your change landed.
            </p>
            <div className="flex flex-wrap gap-3 mt-4">
              <Button asChild variant="outline" size="sm" className="rounded-[4px]" data-testid="admin-help-view-site-button">
                <a href="/" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1.5" /> View the website
                </a>
              </Button>
              <Button asChild variant="outline" size="sm" className="rounded-[4px]" data-testid="admin-help-email-button">
                <a href={CONTACT.emailHref}>
                  <Mail className="h-4 w-4 mr-1.5" /> {CONTACT.email}
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
